import React, { useState } from "react";
import axios from "axios";
import { useLang } from "../i18n/LanguageContext";
import { CheckCircle2, Loader2, AlertCircle, Phone, ArrowRight } from "lucide-react";
import PhoneInput, { isValidPhoneNumber } from "react-phone-number-input";
import "react-phone-number-input/style.css";
import PlaceAutocompleteInput from "./PlaceAutocompleteInput";
import { API_BASE, formatApiError } from "../config/api";

const emptyLocation = () => ({ address: "", lat: null, lng: null, place_id: null });

const BookingForm = ({ compact = false, defaultService = "city" }) => {
  const { t } = useLang();
  const [form, setForm] = useState({
    full_name: "",
    phone: "",
    email: "",
    pickup: emptyLocation(),
    dropoff: emptyLocation(),
    pickup_date: "",
    pickup_time: "",
    passengers: 1,
    luggage: 0,
    notes: "",
  });
  const [status, setStatus] = useState("idle");
  const [errorMsg, setErrorMsg] = useState("");

  const handleChange = (e) => setForm((f) => ({ ...f, [e.target.name]: e.target.value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.phone || !isValidPhoneNumber(form.phone)) {
      setStatus("error");
      setErrorMsg(t.booking?.phoneInvalid || "Numri i telefonit nuk është i vlefshëm.");
      return;
    }
    if (!form.pickup.address?.trim() || form.pickup.address.trim().length < 3) {
      setStatus("error");
      setErrorMsg(t.booking?.pickupRequired || "Zgjidhni vendin e nisjes (adresë ose biznes).");
      return;
    }
    if (!form.dropoff.address?.trim() || form.dropoff.address.trim().length < 3) {
      setStatus("error");
      setErrorMsg(t.booking?.dropoffRequired || "Zgjidhni destinacionin (adresë ose biznes).");
      return;
    }

    setStatus("submitting");
    setErrorMsg("");

    try {
      const payload = {
        full_name: form.full_name.trim(),
        phone: form.phone,
        email: form.email?.trim() || undefined,
        pickup_location: form.pickup.address.trim(),
        dropoff_location: form.dropoff.address.trim(),
        pickup_lat: form.pickup.lat ?? undefined,
        pickup_lng: form.pickup.lng ?? undefined,
        dropoff_lat: form.dropoff.lat ?? undefined,
        dropoff_lng: form.dropoff.lng ?? undefined,
        pickup_date: form.pickup_date,
        pickup_time: form.pickup_time,
        passengers: parseInt(form.passengers, 10) || 1,
        luggage: parseInt(form.luggage, 10) || 0,
        vehicle_type: "sedan",
        service_type: defaultService,
        notes: form.notes?.trim() || undefined,
      };
      if (!payload.email) delete payload.email;
      if (!payload.notes) delete payload.notes;
      if (payload.pickup_lat == null) delete payload.pickup_lat;
      if (payload.pickup_lng == null) delete payload.pickup_lng;
      if (payload.dropoff_lat == null) delete payload.dropoff_lat;
      if (payload.dropoff_lng == null) delete payload.dropoff_lng;

      // /api/bookings is proxied by nginx to the dispatcher backend (port 8001)
      await axios.post(`${API_BASE}/bookings`, payload, {
        headers: { "Content-Type": "application/json" },
        timeout: 30000,
      });

      setStatus("success");
      setForm({
        full_name: "",
        phone: "",
        email: "",
        pickup: emptyLocation(),
        dropoff: emptyLocation(),
        pickup_date: "",
        pickup_time: "",
        passengers: 1,
        luggage: 0,
        notes: "",
      });
    } catch (err) {
      setStatus("error");
      const detail = err.response?.data?.detail;
      setErrorMsg(
        formatApiError(detail, null) ||
          (err.message === "Network Error"
            ? "Lidhja me serverin dështoi. Kontrolloni internetin dhe provoni përsëri."
            : t.booking.error)
      );
    }
  };

  if (status === "success") {
    return (
      <div data-testid="booking-success" className="bg-white border border-[#FFD500] rounded-3xl p-8 sm:p-10 text-center">
        <div className="h-14 w-14 sm:h-16 sm:w-16 bg-[#FFD500] rounded-full flex items-center justify-center mx-auto mb-5">
          <CheckCircle2 className="h-7 w-7 sm:h-8 sm:w-8 text-zinc-900" strokeWidth={2.5} />
        </div>
        <h3 className="font-display text-2xl sm:text-3xl font-black text-zinc-900 mb-3 uppercase">{t.common.success}</h3>
        <p className="text-zinc-600 mb-6">{t.booking.success}</p>
        <button data-testid="booking-new-button" onClick={() => setStatus("idle")} className="bt-btn-outline" type="button">
          {t.common.bookTaxi}
        </button>
      </div>
    );
  }

  return (
    <form data-testid="booking-form" onSubmit={handleSubmit} className={`bg-white ${compact ? "p-5 sm:p-6" : "p-6 sm:p-8 md:p-10"}`}>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-5">
        <div className="sm:col-span-2">
          <input
            data-testid="booking-fullname-input"
            required
            name="full_name"
            value={form.full_name}
            onChange={handleChange}
            placeholder={`${t.booking.fields.fullName} *`}
            className="bt-input"
          />
        </div>

        <div className="sm:col-span-2">
          <PhoneInput
            data-testid="booking-phone-input"
            defaultCountry="AL"
            international
            countryCallingCodeEditable={false}
            value={form.phone}
            onChange={(value) => setForm((f) => ({ ...f, phone: value || "" }))}
            placeholder={`${t.booking.fields.phone} *`}
          />
        </div>

        <div className="sm:col-span-2">
          <input
            data-testid="booking-email-input"
            name="email"
            value={form.email}
            onChange={handleChange}
            placeholder={t.booking.fields.email}
            className="bt-input"
            type="email"
          />
        </div>

        <div>
          <label className="bt-label" htmlFor="booking-pickup">
            {t.booking.fields.pickupLocation} *
          </label>
          <PlaceAutocompleteInput
            id="booking-pickup"
            data-testid="booking-pickup-input"
            required
            value={form.pickup.address}
            onChange={(pickup) => setForm((f) => ({ ...f, pickup }))}
            placeholder={t.booking.placeholders.pickup}
          />
          <p className="text-xs text-zinc-500 mt-1">Kërkoni adresë ose emër biznesi</p>
        </div>
        <div>
          <label className="bt-label" htmlFor="booking-dropoff">
            {t.booking.fields.dropoffLocation} *
          </label>
          <PlaceAutocompleteInput
            id="booking-dropoff"
            data-testid="booking-dropoff-input"
            required
            value={form.dropoff.address}
            onChange={(dropoff) => setForm((f) => ({ ...f, dropoff }))}
            placeholder={t.booking.placeholders.dropoff}
          />
          <p className="text-xs text-zinc-500 mt-1">Kërkoni adresë ose emër biznesi</p>
        </div>

        <div>
          <label className="bt-label">{t.booking.fields.pickupDate} *</label>
          <input
            data-testid="booking-date-input"
            required
            type="date"
            name="pickup_date"
            value={form.pickup_date}
            onChange={handleChange}
            className="bt-input"
          />
        </div>
        <div>
          <label className="bt-label">{t.booking.fields.pickupTime} *</label>
          <input
            data-testid="booking-time-input"
            required
            type="time"
            name="pickup_time"
            value={form.pickup_time}
            onChange={handleChange}
            className="bt-input"
          />
        </div>

        <div>
          <label className="bt-label">{t.booking.fields.passengers}</label>
          <input
            data-testid="booking-passengers-input"
            type="number"
            min="1"
            max="8"
            name="passengers"
            value={form.passengers}
            onChange={handleChange}
            className="bt-input"
          />
        </div>
        <div>
          <label className="bt-label">{t.booking.fields.luggage}</label>
          <input
            data-testid="booking-luggage-input"
            type="number"
            min="0"
            max="10"
            name="luggage"
            value={form.luggage}
            onChange={handleChange}
            className="bt-input"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="bt-label">{t.booking.fields.notes}</label>
          <textarea
            data-testid="booking-notes-input"
            name="notes"
            value={form.notes}
            onChange={handleChange}
            placeholder={t.booking.placeholders.notes}
            rows={3}
            className="bt-input resize-none"
          />
        </div>
      </div>

      {status === "error" && (
        <div data-testid="booking-error" className="mt-5 flex items-center gap-2 text-red-600 text-sm font-semibold">
          <AlertCircle className="h-4 w-4 flex-shrink-0" /> {errorMsg}
        </div>
      )}

      <div className="mt-6 sm:mt-7 flex flex-col sm:flex-row gap-3">
        <button
          type="submit"
          data-testid="booking-submit-button"
          disabled={status === "submitting"}
          className="bt-btn-primary flex-1"
        >
          {status === "submitting" ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" /> {t.common.submitting}
            </>
          ) : (
            <>
              {t.booking.submit} <ArrowRight className="h-4 w-4" />
            </>
          )}
        </button>
        <a
          href="tel:+355682222286"
          data-testid="booking-call-fallback"
          className="bt-btn-outline whitespace-nowrap"
        >
          <Phone className="h-4 w-4" /> +355 68 22 22 286
        </a>
      </div>
    </form>
  );
};

export default BookingForm;
